/**
 * Le fond d'une page de l'éditeur images : son calque `bg`, plein cadre, sous tous les
 * autres. Une forme (FabRect) dont le remplissage est une couleur ou un dégradé — le même
 * remplissage que les formes —, ou un ImageFrame pour une image. Sans calque `bg`, la page
 * est transparente.
 *
 * Le fond appartient à la page, ce n'est jamais un élément : inerte au ciblage (la lib le
 * veut ainsi pour tout calque `bg`), il se choisit en sélectionnant la page.
 */
import type { LayerData } from "@frabr/fabric-editor";
import type { ImageMeta } from "./composition_document";
import { fabricFillToCss, fabricGradient, parseColorValue } from "./color_value";

export const BG_LAYER_ID = "bg";

/** Ce que la barre de la page montre du fond. */
export interface BackgroundState {
  /** `html` : LEGACY_ERB — le gabarit HTML d'une page de template (famille ERB), hors du canvas. */
  kind: "none" | "color" | "image" | "html"
  /** Une valeur CSS : la pastille de la barre. */
  css?: string
}

type Layer = LayerData & Record<string, any>

/** Le calque `bg` d'une liste de calques. */
export function backgroundOf(layers: LayerData[]): Layer | undefined {
  return (layers as Layer[]).find((layer) => layer.layerId === BG_LAYER_ID);
}

export function backgroundStateOf(layers: LayerData[]): BackgroundState {
  const bg = backgroundOf(layers);
  if (!bg) return { kind: "none" };
  if (bg.image) return { kind: "image" };
  return { kind: "color", css: fabricFillToCss(bg.fill) };
}

/** Un fond couleur (ou dégradé) : une forme plein cadre, verrouillée. `css` : la valeur du
 *  color picker (hex, rgba, linear-gradient). */
export function colorBackgroundLayer(width: number, height: number, css: string): Layer {
  const value = parseColorValue(css);
  const fill = value.kind === "gradient" ? fabricGradient(value.gradient) : value.kind === "solid" ? value.color : "transparent";
  return {
    type: "Rect", layerId: BG_LAYER_ID, layerType: "shape",
    left: width / 2, top: height / 2, width, height, angle: 0, scaleX: 1, scaleY: 1,
    strokeWidth: 0, opacity: 1, fill,
    lockMode: "full", selectable: false, evented: false,
  } as unknown as Layer;
}

/** Un fond image : un ImageFrame plein cadre (cover), recadrable une fois la page sélectionnée. */
export function imageBackgroundLayer(
  width: number,
  height: number,
  src: string,
  { meta = {}, bindings }: { meta?: ImageMeta; bindings?: object } = {},
): Layer {
  const bg: Layer = {
    type: "ImageFrame", layerId: BG_LAYER_ID,
    // L'identité du média (`content_medium_id`) voyage avec sa source — règle du dialecte.
    image: { ...meta, src, cropX: 0.5, cropY: 0.5, scale: 1 },
    left: width / 2, top: height / 2, frameWidth: width, frameHeight: height,
    angle: 0, scaleX: 1, scaleY: 1, clipShape: "rect", lockMode: "position",
    selectable: false, evented: false,
  } as unknown as Layer;
  if (bindings) bg.bindings = bindings as Layer["bindings"];
  return bg;
}

/** Le même fond, recalé plein cadre sur de nouvelles dimensions (changement de format). */
export function refitBackgroundLayer(bg: Layer, width: number, height: number): Layer {
  if (bg.image) {
    const { cropX: _x, cropY: _y, offsetX: _ox, offsetY: _oy, scale: _s, src, ...meta } = bg.image;
    return imageBackgroundLayer(width, height, src, { meta, bindings: bg.bindings });
  }
  return { ...bg, left: width / 2, top: height / 2, width, height };
}
